import { TrendingUp, Users, Lightbulb, Target, HeartHandshake, Trophy, Linkedin, ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { FadeUp } from "@/components/Reveal";
import { IMAGES, CAREER_STATS, CULTURE_OFFER, LINKEDIN_URL } from "@/data/content";
import SEO from "@/components/SEO";

const ICONS = { TrendingUp, Users, Lightbulb, Target, HeartHandshake, Trophy };

const Careers = () => (
  <>
    <SEO
      title="Careers"
      description="Build your career in agriculture innovation with Seed Park India — a values-driven, farmer-first hybrid seed company. An MS Group company."
    />
    <PageHero
      eyebrow="Careers"
      titleLines={["Build your career in", "agriculture innovation."]}
      description="At Seed Park, we're not just growing crops — we're cultivating careers. Join a team transforming Indian agriculture through cutting-edge seed technology and farmer-centric solutions."
      image={IMAGES.lab1}
    />

    <section data-testid="careers-why-join" className="py-28 lg:py-36">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="Why Join Us"
            titleLines={["Grow with the people", "who grow the nation."]}
            description="We believe in nurturing talent, fostering innovation, and creating an environment where every team member can thrive and make a meaningful impact on millions of farmers' lives."
          />
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:col-span-7">
          {CAREER_STATS.map((s, i) => (
            <FadeUp key={s.value} delay={i * 0.08} className="h-full">
              <div className="h-full border border-[#123C6B]/10 bg-forest-900 p-8" data-testid={`career-stat-${i}`}>
                <p className="font-display text-5xl font-black text-brand-400">{s.value}</p>
                <p className="mt-3 text-sm leading-relaxed text-mist">{s.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="careers-culture" className="bg-forest-900 py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading align="center" eyebrow="Our Culture" titleLines={["What we offer."]} />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {CULTURE_OFFER.map((c, i) => {
            const Icon = ICONS[c.icon];
            return (
              <FadeUp key={c.title} delay={(i % 3) * 0.08} className="h-full">
                <div
                  className="h-full border border-[#123C6B]/10 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(18,60,107,0.12)]"
                  data-testid={`culture-offer-${i}`}
                >
                  <Icon className="h-7 w-7 text-brand-400" />
                  <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-mist">{c.text}</p>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>

    <section data-testid="careers-cta" className="py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <div className="flex flex-col items-start justify-between gap-8 bg-corp-700 p-10 sm:p-14 lg:flex-row lg:items-center">
            <div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">Ready to Grow With Us?</h2>
              <p className="mt-3 max-w-xl text-base leading-relaxed text-white/75">
                Explore opportunities and connect with us on LinkedIn to stay updated on openings across research, production and field teams.
              </p>
            </div>
            <div className="shrink-0">
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="careers-linkedin-btn"
                className="group flex items-center gap-3 bg-corp-400 px-8 py-4 text-sm font-bold text-white transition-colors hover:bg-corp-500"
              >
                <Linkedin className="h-5 w-5" />
                View Openings on LinkedIn
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Follow Seed Park on LinkedIn</p>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  </>
);

export default Careers;
